import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import api from "../services/ApiService";
import { extractList } from "../utils/extractList";
import { parseApiError } from "../utils/errors";
import { parseJwt, isTokenExpired, getTokenUserId } from "../utils/jwt";
import websocketNotificationService from "../services/WebsocketNotificationService";
import { cleanEmail } from "../utils/cleanEmail";
import YandexMetricaService from "../services/YandexMetricaService";

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  phone_number?: string;
  avatar_url?: string;
  address?: string;
  city?: string;
  country?: string;
  bio?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isNewUser: boolean;
  error: string | null;
  clearError: () => void;
  clearNewUserFlag: () => void;
  updateUser: (
    data: Partial<
      User & {
        avatar_url?: string;
        phone_number?: string;
        address?: string;
        city?: string;
        country?: string;
        bio?: string;
      }
    >,
  ) => void;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  register: (userData: {
    email: string;
    name: string;
    password: string;
    referral_code?: string;
    captcha_token?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  refreshToken: () => Promise<boolean>;
  checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isNewUser, setIsNewUser] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Функция проверки и обновления токена
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch("/api/v1/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const newToken = data.access_token || data.token;

        if (newToken) {
          localStorage.setItem("token", newToken);
          setToken(newToken);

          // Обновляем WebSocket соединение с новым токеном
          websocketNotificationService.refreshGlobal(newToken);

          return true;
        }
      }
      return false;
    } catch (err) {
      console.error("Token refresh failed:", err);
      return false;
    }
  }, []);

  // Функция загрузки данных пользователя
  const loadUserProfile = useCallback(
    async (authToken: string): Promise<User | null> => {
      try {
        const payload = parseJwt(authToken);

        if (payload?.user_id && payload.email) {
          return {
            id: payload.user_id,
            email: cleanEmail(payload.email),
            name: payload.name || cleanEmail(payload.email).split("@")[0],
            role: payload.role || "client",
          };
        }

        // Если не получилось из токена, пробуем через API
        try {
          const userId = getTokenUserId(authToken);

          if (userId) {
            const response = await api
              .getAxiosInstance()
              .get(`/users/${userId}/profile`);

            // Бэкенд возвращает: { data: { user: {...}, referral_bonus_used: ... } }
            if (
              response.data &&
              response.data.data &&
              response.data.data.user
            ) {
              const userData = response.data.data.user;

              // Проверяем наличие обязательных полей
              if (!userData || !userData.email) {
                console.warn(
                  "User profile missing or missing email field:",
                  userData,
                );
                return null;
              }

              return {
                id: userData.id,
                email: cleanEmail(userData.email),
                name: userData.name || cleanEmail(userData.email).split("@")[0],
                role: userData.default_role || userData.role || "client",
                phone_number: userData.phone_number,
                avatar_url: userData.avatar_url,
                address: userData.address,
                city: userData.city,
                country: userData.country,
                bio: userData.bio,
              };
            } else {
              console.warn("Unexpected API response structure:", response.data);
            }
          } else {
            console.warn("User ID not found in token payload");
          }
        } catch (e: any) {
          // Более подробная обработка ошибок API
          const errorMessage =
            e.response?.data?.error || e.message || "Unknown error";
          const errorStatus = e.response?.status;
          console.warn(
            `Failed to fetch user profile from API (status: ${errorStatus}): ${errorMessage}`,
          );
        }

        return null;
      } catch (err) {
        console.error("Failed to load user profile:", err);
        return null;
      }
    },
    [],
  );

  // Проверка токена на валидность
  const isTokenValid = useCallback((authToken: string): boolean => {
    return !isTokenExpired(authToken, 0);
  }, []);

  // Проверка, нужно ли обновить токен
  const shouldRefreshToken = useCallback((authToken: string): boolean => {
    return isTokenExpired(authToken, 300);
  }, []);

  // Инициализация при загрузке
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem("token");
      const wasNewUser = localStorage.getItem("isNewUser") === "true";

      if (storedToken) {
        if (isTokenValid(storedToken)) {
          setToken(storedToken);
          setIsNewUser(wasNewUser);

          // Загружаем данные пользователя
          const userData = await loadUserProfile(storedToken);
          if (userData) {
            setUser(userData);

            // Инициализируем глобальное WebSocket соединение
            websocketNotificationService.initializeGlobal(storedToken);

            // Проверяем, нужно ли обновить токен
            if (shouldRefreshToken(storedToken)) {
              await refreshToken();
            }
          } else {
            // Не удалось загрузить профиль — токен невалидный для текущего пользователя
            localStorage.removeItem("token");
            localStorage.removeItem("isNewUser");
            setToken(null);
          }
        } else {
          // Токен невалиден, очищаем
          localStorage.removeItem("token");
          localStorage.removeItem("isNewUser");
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, [isTokenValid, shouldRefreshToken, refreshToken, loadUserProfile]);

  const login = async (
    email: string,
    password: string,
    rememberMe: boolean = false,
  ): Promise<{ success: boolean; error?: string }> => {
    setError(null);

    try {
      const response = await api.login(email, password);

      if (
        response.data &&
        (response.data.token || response.data.access_token)
      ) {
        const accessToken = response.data.token || response.data.access_token;
        const userData = response.data.user;

        localStorage.removeItem("fsm_org_id");
        // Fresh sign-in starts with the primary role, not a stale in-app
        // role switch from a previous session (it would misroute admins).
        if (userData?.id != null) {
          localStorage.removeItem(`selected_role_${userData.id}`);
        }

        // Сохраняем токен
        localStorage.setItem("token", accessToken);
        if (rememberMe) {
          localStorage.setItem(
            "tokenExpiry",
            String(Date.now() + 30 * 24 * 60 * 60 * 1000),
          );
        } else {
          localStorage.removeItem("tokenExpiry");
        }

        setToken(accessToken);
        setUser(userData);

        // Report login to Yandex.Metrica
        if (userData?.id) {
          YandexMetricaService.setUserId(userData.id);
          YandexMetricaService.reportEvent("user_login", {
            email,
            role: userData.role,
          });
        }

        // Инициализируем глобальное WebSocket соединение при входе
        websocketNotificationService.initializeGlobal(accessToken);

        // Auto-select first organization if user has any
        try {
          const orgsRes = await api.listOrganizations();
          const orgs = extractList(orgsRes.data);
          if (!orgsRes.error && orgs.length > 0) {
            const firstOrg = orgs[0];
            const selectRes = await api.selectOrg(firstOrg.id);
            if (!selectRes.error && selectRes.data?.access_token) {
              localStorage.setItem("token", selectRes.data.access_token);
              localStorage.setItem("refresh_token", selectRes.data.refresh_token);
              localStorage.setItem("fsm_org_id", String(firstOrg.id));
              setToken(selectRes.data.access_token);
            }
          }
        } catch (e) {
          // Ignore org selection errors
          console.warn("Failed to auto-select organization:", e);
        }

        return { success: true };
      } else {
        const errorMsg = response.error ?? undefined;
        setError(errorMsg ?? null);
        return { success: false, error: errorMsg };
      }
    } catch (err: any) {
      const parsedError = parseApiError(err);
      setError(parsedError.message);
      return { success: false, error: parsedError.message };
    }
  };

  const register = async (userData: {
    email: string;
    name: string;
    password: string;
    referral_code?: string;
    captcha_token?: string;
  }): Promise<{ success: boolean; error?: string }> => {
    setError(null);

    try {
      const response = await api.register(userData);

      if (
        response.data &&
        (response.data.token || response.data.access_token)
      ) {
        const accessToken = response.data.token || response.data.access_token;
        const user = response.data.user;

        localStorage.setItem("token", accessToken);
        localStorage.setItem("isNewUser", "true");

        setToken(accessToken);
        setUser(user);

        // Report registration to Yandex.Metrica
        YandexMetricaService.reportEvent("user_registration", {
          email: userData.email,
          referral_code: userData.referral_code,
        });

        if (user?.id) {
          YandexMetricaService.setUserId(user.id);
        }

        // Инициализируем глобальное WebSocket соединение при регистрации
        websocketNotificationService.initializeGlobal(accessToken);

        return { success: true };
      } else {
        const errorMsg = response.error ?? undefined;
        setError(errorMsg ?? null);
        return { success: false, error: errorMsg };
      }
    } catch (err: any) {
      const parsedError = parseApiError(err);
      setError(parsedError.message);
      return { success: false, error: parsedError.message };
    }
  };

  const logout = useCallback(async () => {
    try {
      // Вызываем API для завершения сессии на бэкенде
      // Используем fetch() вместо axios, чтобы обойти 401 interceptor
      const storedToken = localStorage.getItem("token");
      if (storedToken) {
        await fetch("/api/v1/auth/logout", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${storedToken}`,
          },
        }).catch(() => {});
      }
    } catch (err) {
      console.error("Logout API error:", err);
    } finally {
      // Очищаем ВСЁ в любом случае
      localStorage.clear(); // Очищаем весь localStorage

      // Report logout to Yandex.Metrica
      if (user?.id) {
        YandexMetricaService.reportEvent("user_logout", {
          user_id: user.id,
        });
        YandexMetricaService.clearUserId();
      }

      // Отключаем глобальное WebSocket соединение
      websocketNotificationService.disconnectGlobal();

      // Сбрасываем состояние
      setToken(null);
      setUser(null);
      setIsNewUser(false);
      setError(null);

      // Уведомляем компоненты о разлогинивании для отключения WebSocket
      window.dispatchEvent(new CustomEvent("auth-logout"));
    }
  }, [user]);

  // Обработчик события истечения токена.
  //
  // Объявлен после `logout`, чтобы не обращаться к переменной до её
  // инициализации, и зависит от неё, чтобы подписаться на актуальную версию.
  useEffect(() => {
    const handleTokenExpired = async () => {
      const storedToken = localStorage.getItem("token");

      if (!storedToken) {
        // Токена нет — refresh нечего обновлять. Состояние нужно сбросить
        // явно, иначе приложение остаётся «авторизованным» без credentials.
        await logout();
        return;
      }

      const success = await refreshToken();
      if (!success) {
        // Refresh не удался (401/истёкший refresh token) — выходим.
        await logout();
      }
    };

    window.addEventListener("auth-token-expired", handleTokenExpired);
    return () =>
      window.removeEventListener("auth-token-expired", handleTokenExpired);
  }, [refreshToken, logout]);

  const updateUser = useCallback(
    (
      data: Partial<
        User & {
          avatar_url?: string;
          phone_number?: string;
          address?: string;
          city?: string;
          bio?: string;
        }
      >,
    ) => {
      setUser((prevUser) => {
        if (!prevUser) return prevUser;
        return {
          ...prevUser,
          ...data,
        };
      });
      // Также сохраняем обновлённые данные в localStorage для персистентности
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          localStorage.setItem(
            "user",
            JSON.stringify({ ...parsedUser, ...data }),
          );
        } catch (e) {
          console.warn("Failed to update stored user data:", e);
        }
      }
    },
    [],
  );

  const checkAuth = async (): Promise<boolean> => {
    const storedToken = localStorage.getItem("token");

    if (!storedToken) {
      return false;
    }

    if (!isTokenValid(storedToken)) {
      // Пытаемся обновить токен
      const refreshed = await refreshToken();
      return refreshed;
    }

    return true;
  };

  const clearError = () => setError(null);

  const clearNewUserFlag = () => {
    localStorage.removeItem("isNewUser");
    setIsNewUser(false);
  };

  const value = useMemo(
    () => ({
      user,
      token,
      isAuthenticated: !!token,
      isLoading,
      isNewUser,
      error,
      clearError,
      clearNewUserFlag,
      updateUser,
      login,
      logout,
      register,
      refreshToken,
      checkAuth,
    }),
    [
      user,
      token,
      isLoading,
      isNewUser,
      error,
      clearError,
      clearNewUserFlag,
      updateUser,
      login,
      logout,
      register,
      refreshToken,
      checkAuth,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
